import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BarChart3, TrendingDown, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { useWeeklyStudentDigest } from '../hooks/useWeeklyStudentDigest';
import { ROUTES } from '../../../utils/constants';
import {
  DashboardCardSkeleton,
  DashboardEmptyState,
  DashboardInlineError,
} from './dashboardUi';

export function WeeklyDigestCard() {
  const { t } = useTranslation();
  const { digest, loading, error, reload } = useWeeklyStudentDigest();

  const score = useMemo(() => {
    const raw = digest?.engagement?.engagement_score;
    if (raw == null) return null;
    return Math.max(0, Math.min(100, Math.round(Number(raw))));
  }, [digest]);

  const strength = digest?.highlights?.strongest_skill || digest?.highlights?.best_can_do || null;
  const improvement = digest?.highlights?.needs_focus_skill || null;

  if (loading) {
    return <DashboardCardSkeleton />;
  }

  if (error) {
    return (
      <Card className="border-purple-200/60 dark:border-purple-500/25">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2 text-slate-900 dark:text-white">
            <BarChart3 className="h-5 w-5 text-purple-600 dark:text-pink-400" aria-hidden />
            {t('dashboard.studentHome.weeklyDigest')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <DashboardInlineError
            message={t('dashboard.studentHome.weeklyDigestUnavailable')}
            onRetry={() => void reload()}
          />
          <Link
            to={ROUTES.STUDENT_ANALYTICS}
            className="mt-4 inline-flex text-sm font-semibold text-purple-600 dark:text-purple-300"
          >
            {t('dashboard.studentHome.viewMyReportArrow')}
          </Link>
        </CardContent>
      </Card>
    );
  }

  if (!digest) {
    return (
      <Card className="border-purple-200/60 dark:border-purple-500/25">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2 text-slate-900 dark:text-white">
            <BarChart3 className="h-5 w-5 text-purple-600 dark:text-pink-400" aria-hidden />
            {t('dashboard.studentHome.weeklyDigest')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <DashboardEmptyState>
            {t('dashboard.studentHome.weeklyDigestEmpty')}
          </DashboardEmptyState>
          <Link
            to={ROUTES.STUDENT_ANALYTICS}
            className="inline-flex text-sm font-semibold text-purple-600 dark:text-purple-300"
          >
            {t('dashboard.studentHome.viewMyReportArrow')}
          </Link>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-purple-200/60 bg-gradient-to-br from-white to-indigo-50/30 dark:border-purple-500/25 dark:from-gray-800 dark:to-indigo-950/20 h-full">
      <CardHeader className="flex flex-row items-start justify-between gap-3 pb-2">
        <CardTitle className="text-base flex items-center gap-2 text-slate-900 dark:text-white">
          <BarChart3 className="h-5 w-5 text-purple-600 dark:text-pink-400" aria-hidden />
          {t('dashboard.studentHome.weeklyDigest')}
        </CardTitle>
        <Link
          to={ROUTES.STUDENT_ANALYTICS}
          className="shrink-0 text-xs font-semibold text-purple-700 hover:text-purple-900 dark:text-purple-300"
        >
          {t('dashboard.studentHome.viewMyReport')}
        </Link>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-4">
          <div
            className="relative flex h-16 w-16 shrink-0 items-center justify-center rounded-full border-4 border-purple-200 dark:border-purple-700"
            role="img"
            aria-label={
              score != null
                ? t('dashboard.studentHome.engagementScoreAria', { score })
                : t('dashboard.studentHome.engagementUnavailableAria')
            }
          >
            <span className="text-lg font-black text-purple-700 dark:text-purple-200">
              {score != null ? score : '—'}
            </span>
          </div>
          <div className="min-w-0 text-sm">
            <p className="font-semibold text-slate-900 dark:text-white">
              {t('dashboard.studentHome.weeklyEngagementScore')}
            </p>
            <p className="text-slate-600 dark:text-slate-400">
              {digest.engagement?.engagement_label ?? '—'} ·{' '}
              {t('dashboard.studentHome.activeDays', {
                count: digest.engagement?.active_days ?? 0,
              })}
            </p>
          </div>
        </div>
        {strength ? (
          <div className="flex items-start gap-2 rounded-xl border border-emerald-200/80 bg-emerald-50/50 dark:border-emerald-800/50 dark:bg-emerald-950/20 px-3 py-2.5">
            <TrendingUp className="h-4 w-4 shrink-0 text-emerald-600 mt-0.5" aria-hidden />
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-emerald-800 dark:text-emerald-300">
                {t('dashboard.studentHome.strength')}
              </p>
              <p className="text-sm text-slate-900 dark:text-white">{strength}</p>
            </div>
          </div>
        ) : null}
        {improvement ? (
          <div className="flex items-start gap-2 rounded-xl border border-amber-200/80 bg-amber-50/50 dark:border-amber-800/50 dark:bg-amber-950/20 px-3 py-2.5">
            <TrendingDown className="h-4 w-4 shrink-0 text-amber-600 mt-0.5" aria-hidden />
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-300">
                {t('dashboard.studentHome.focusNext')}
              </p>
              <p className="text-sm text-slate-900 dark:text-white">{improvement}</p>
            </div>
          </div>
        ) : null}
        {!strength && !improvement ? (
          <DashboardEmptyState className="py-6">
            {t('dashboard.studentHome.weeklyDigestKeepLearning')}
          </DashboardEmptyState>
        ) : null}
      </CardContent>
    </Card>
  );
}
